import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { AppRole } from '@infras/shared-types';
import { MODE_OPTIONS, type Mode, currentMode, setMode } from '../lib/theme';

/**
 * Top-right account dropdown: an Appearance row (System / Light / Dark — System
 * follows the OS live) · Settings · Sign out. The app is fixed to its default
 * theme (Afterglow); only light/dark/system is user-selectable. The Appearance
 * row keeps the menu open (try looks without reopening); Sign out closes normally.
 */
export function AccountMenu({
  email,
  role,
  onLogout,
}: {
  email: string;
  role: AppRole;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [mode, setModeState] = useState<Mode>(() => currentMode());
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pickMode = (value: Mode) => {
    setMode(value);
    setModeState(value);
  };

  return (
    <div className="account-menu" ref={ref}>
      <button
        className="account-trigger"
        data-testid="account-menu"
        onClick={() => setOpen((v) => !v)}
      >
        {email} <span className="badge">{role}</span> ▾
      </button>
      {open && (
        <div className="account-panel">
          <div className="account-section">Appearance</div>
          <div className="account-modes" data-testid="mode-row" role="group" aria-label="Appearance">
            {MODE_OPTIONS.map((m) => (
              <button
                key={m.value}
                data-testid={`mode-${m.value}`}
                className={mode === m.value ? 'active' : ''}
                aria-pressed={mode === m.value}
                onClick={() => pickMode(m.value)}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="account-sep" />
          <button
            data-testid="account-settings"
            onClick={() => {
              setOpen(false);
              navigate('/settings');
            }}
          >
            <span className="account-check" />
            Settings
          </button>
          <button data-testid="sign-out" onClick={onLogout}>
            <span className="account-check">⎋</span>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
